/*
 * ce-preadd.js — puts the store's automatic extras in the cart in the SAME gesture as the customer's add-to-cart.
 *
 * Who uses it: the PDP free gift (snippets/ce-free-gift.liquid) and the cart drawer add-on when it is on by default
 * (layout/theme.liquid). Both drop a marker: <span data-ce-preadd-variant="123" hidden> — Liquid renders a marker
 * ONLY when that variant is not already in the cart, so the page itself is the cart snapshot and no read is needed.
 *
 * WHY THIS SHAPE (baseline measured 2026-09-02: GET /cart.js 170–720 ms → POST update 630–710 ms → POST add ~1 s
 * = 2.0–2.5 s before the drawer opened):
 *   - No pre-flight read: /cart/update.js writes ABSOLUTE quantities ({id: 1}), so sending it when the extra is
 *     already in the cart changes nothing. The read bought certainty we get for free from idempotence.
 *   - The extras POST runs ONCE: after it succeeds the ids are remembered (`believedIn`) and the next add-to-carts
 *     go straight through — zero interception cost. The belief is dropped when the cart is edited anywhere else
 *     (Dawn pubsub, source ≠ product-form: a removed gift comes back on the next add-to-cart) and kept in sync with
 *     the drawer switch (`ce:addon-changed`).
 *   - Extras still go in BEFORE the theme's own add: the drawer is rendered once, complete (the gift line is
 *     visible in it). Riding inside the theme's /cart/add.js was measured to kill the customer's Meta AddToCart.
 *   - No speculative call on pointerdown/touchstart: a scroll that starts on the button would mutate the cart of
 *     someone who never added anything.
 *   - The customer's add is never at risk: whatever happens here, the form is submitted right after.
 */
(() => {
  if (window.cePreAdd) return; // the file can be included by both the PDP and the layout
  window.cePreAdd = true;

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const believedIn = new Set();

  const wanted = () =>
    [...document.querySelectorAll('[data-ce-preadd-variant]')]
      .map((node) => String(node.dataset.cePreaddVariant))
      .filter((id) => id && id !== '0' && !believedIn.has(id));

  // the drawer switch tells us what it just did, so we never send a redundant update for it
  document.addEventListener('ce:addon-changed', (event) => {
    const { variantId, on } = event.detail || {};
    if (!variantId) return;
    if (on) believedIn.add(String(variantId));
    else believedIn.delete(String(variantId));
  });

  // the cart was edited outside an add-to-cart (drawer quantity/remove): re-verify on the next add — one idempotent
  // POST, which also brings a removed gift back, as intended
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source !== 'product-form') believedIn.clear();
    });
  }

  const showLoading = (form) => {
    // the theme paints its spinner when IT starts; we start earlier, so paint it now.
    // NEVER set aria-disabled here: Dawn's own submit handler returns immediately when it is "true".
    const button = form.querySelector('[type="submit"]');
    if (!button) return;
    button.classList.add('loading');
    const spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');
  };

  const ensure = async (ids) => {
    const updates = {};
    ids.forEach((id) => {
      updates[id] = 1;
    });
    try {
      const response = await fetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      const cart = await response.json();
      if (response.ok && !cart.status) {
        const inCart = new Set((cart.items || []).map((item) => String(item.variant_id)));
        ids.forEach((id) => {
          if (inCart.has(id)) believedIn.add(id);
        });
      }
    } catch (error) {
      /* the customer's add runs next whatever happens here */
    }
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!form || !form.action || !/\/cart\/add/.test(form.action)) return;
      if (form.dataset.cePreaddPass === '1') {
        delete form.dataset.cePreaddPass; // our own re-submit: hand it over to the theme
        return;
      }
      const ids = wanted();
      if (!ids.length) return; // fast path: nothing to add, the theme takes over untouched

      event.preventDefault();
      event.stopImmediatePropagation();
      showLoading(form);
      ensure(ids).then(() => {
        form.dataset.cePreaddPass = '1';
        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      });
    },
    true
  );
})();
